import { useQuery } from '@tanstack/react-query';

import { getKidSub } from '@/auth/authStore';
import { api } from '@/lib/api';

/**
 * What the signed-in learner may see and use in Study (`GET /study/me/availability`;
 * study-institution-m1-design.md §2.3, §3.4). The server computes every value; the app renders.
 */

export type StudyTutorState =
  | 'available'
  | 'disabled_platform'
  | 'disabled_institution'
  | 'disabled_class'
  | 'exam_window';

export interface StudySubjectAvailability {
  subject_key: string;
  enabled: boolean;
  tools_enabled: boolean;
  practice_enabled: boolean;
  generated_practice_enabled: boolean;
  tutor: {
    state: StudyTutorState;
    max_hint_level: number;
    resumes_at: string | null;
    exam_label: string | null;
  };
}

export interface StudyAvailability {
  paid_ai_funding?: 'family_wallet' | 'institution_pool' | null;
  study_visible: boolean;
  hsc_plan_visible: boolean;
  learner_source: 'family' | 'institution' | 'walk_in';
  year_level: number | null;
  policy: {
    institution_id: string | null;
    uploads_enabled: boolean;
    /** S10: confirm the source text before Tutor and generated practice. Missing = not required. */
    require_source_confirmation?: boolean;
    disabled_tool_keys: string[];
    subjects: StudySubjectAvailability[];
    default_subject: Omit<StudySubjectAvailability, 'subject_key'>;
  };
}

/** Prefix for invalidation. The query key adds the learner id (one answer per learner). */
export const STUDY_AVAILABILITY_QUERY_KEY = ['study-availability'] as const;
export const studyAvailabilityQueryKey = (kidId: string | null = getKidSub()) =>
  [...STUDY_AVAILABILITY_QUERY_KEY, kidId] as const;
const STUDY_AVAILABILITY_STALE_MS = 30_000;

export const getStudyAvailability = () => api<StudyAvailability>('/study/me/availability');

export function useStudyAvailability(enabled = true) {
  return useQuery({
    queryKey: studyAvailabilityQueryKey(),
    queryFn: getStudyAvailability,
    staleTime: STUDY_AVAILABILITY_STALE_MS,
    retry: false,
    enabled,
  });
}

export function subjectAvailability(
  availability: StudyAvailability | undefined,
  subjectKey: string,
): StudySubjectAvailability | null {
  // An answer without a policy (an older backend) counts as unknown, never as "off".
  const policy = availability?.policy;
  if (!policy?.subjects || !policy.default_subject) return null;
  return (
    policy.subjects.find((subject) => subject.subject_key === subjectKey) ?? {
      subject_key: subjectKey,
      ...policy.default_subject,
    }
  );
}

/** True only when the server says the learner's Institution or class turned the subject off. */
export function studySubjectHidden(
  availability: StudyAvailability | undefined,
  subjectKey: string,
): boolean {
  return subjectAvailability(availability, subjectKey)?.enabled === false;
}

/** True only when the server lists the tool key as turned off for this learner. */
export function studyToolHidden(availability: StudyAvailability | undefined, toolKey: string) {
  return availability?.policy?.disabled_tool_keys?.includes(toolKey) === true;
}

/** Learn navigation paths that only some learners see (SIR-HYG-01). */
export const STUDY_NAV_PATH = '/learn/study';
export const HSC_PLAN_NAV_PATH = '/learn/hsc';

/**
 * Keep Study discoverable while availability loads or fails. Only an explicit server refusal
 * hides Study; this never grants access to an action. HSC Plan still needs explicit eligibility.
 */
export function filterStudyNavigation<T extends { to: string }>(
  items: readonly T[],
  availability: StudyAvailability | undefined,
): T[] {
  return items.filter((item) => {
    if (item.to === STUDY_NAV_PATH) return availability?.study_visible !== false;
    if (item.to === HSC_PLAN_NAV_PATH) return availability?.hsc_plan_visible === true;
    return true;
  });
}
